<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div>
        <!-- Header -->
        <div class="mb-8 flex flex-col items-center">
          <Title variant="indigo" align="center">Dashboard</Title>
          <div class="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm">
            <span class="text-gray-500">Subject</span>
            <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 font-medium">{{ configuredData?.subid || processingResults?.subid || 'N/A' }}</span>
            <span class="text-gray-300">•</span>
            <span class="text-gray-500">Dataset</span>
            <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 font-medium">{{ configuredData?.dataset_id || configuredData?.dataset_identifier || processingResults?.dataset_identifier || 'N/A' }}</span>
            <span class="text-gray-300">•</span>
            <span class="text-gray-500">Status</span>
            <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-900 font-medium">{{ getStatusText() }}</span>
          </div>
        </div>

        <!-- Data Overview Card -->
        <div class="bg-white rounded-lg shadow border-4 border-gray-300 p-6 mb-8 w-full max-w-7xl mx-auto">
          <!-- Data Metrics Grid -->
          <div class="flex justify-center gap-8 mb-6">
            <!-- Sampling Rate -->
            <div class="relative group bg-gray-50 border-2 border-gray-200 rounded-lg p-4 hover:bg-gray-100 hover:border-gray-300 transition-all duration-200 w-64">
              <div class="flex flex-col items-center text-center">
                <div class="mb-1">
                  <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-sm font-medium text-gray-900">Sampling Rate</h3>
                  <p class="text-lg font-bold text-purple-600">{{ displaySamplingRate }} /min</p>
                </div>
              </div>
              <!-- Tooltip -->
              <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                <div class="text-center">
                  <div class="mb-1">When uploaded, data is configured to 1 reading per minute.</div>
                  <div v-if="configuredData?.initialSamplingRate === configuredData?.finalSamplingRate">
                    The data you provided was already 1 reading per minute, so no changes were made.
                  </div>
                  <div v-else>
                    The data had a sampling rate of {{ configuredData?.initialSamplingRate || 0 }} readings per minute, and this has been converted to 1 reading per minute.
                  </div>
                </div>
                <div class="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>

            <!-- Total Observations -->
            <div v-if="displayTotalObservations > 0" class="relative group bg-gray-50 border-2 border-gray-200 rounded-lg p-4 hover:bg-gray-100 hover:border-gray-300 transition-all duration-200 w-64">
              <div class="flex flex-col items-center text-center">
                <div class="mb-1">
                  <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-sm font-medium text-gray-900">Total Observations</h3>
                  <p class="text-lg font-bold text-blue-600">{{ displayTotalObservations }}</p>
                </div>
              </div>
              <!-- Tooltip -->
              <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                <div class="text-center">
                  The total number of readings (rows) in the configured data.
                </div>
                <div class="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>

            <!-- Day Count -->
            <div class="relative group bg-gray-50 border-2 border-gray-200 rounded-lg p-4 hover:bg-gray-100 hover:border-gray-300 transition-all duration-200 w-64">
              <div class="flex flex-col items-center text-center">
                <div class="mb-1">
                  <svg class="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                </div>
                <div>
                  <h3 class="text-sm font-medium text-gray-900">Day Count</h3>
                  <p class="text-lg font-bold text-orange-600">{{ displayDayCount }}</p>
                </div>
              </div>
              <!-- Tooltip -->
              <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                The count of unique dates within the dataset
                <div class="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>
          </div>

          <!-- Processing Section -->
          <div class="border-t border-gray-200 pt-6 mb-6">
            <div class="flex justify-center">
              <div class="w-full max-w-7xl">
                <!-- Show Process button when not processing and no results exist -->
                <div v-if="!showStatusBar && !processingResults" class="flex justify-center">
                  <button @click="showProcessSettingsModal = true" class="w-[85%] group bg-gray-700 border-2 border-gray-500 rounded-lg shadow-lg hover:shadow-xl hover:border-blue-400 transition-all duration-300 p-6">
                    <div class="flex items-center justify-center text-center">
                      <div class="flex-shrink-0">
                        <div class="w-8 h-8 bg-gray-600 rounded-lg flex items-center justify-center group-hover:bg-blue-500 transition-colors duration-200">
                          <svg class="w-5 h-5 text-blue-400 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2 12h4l3-8 4 18 3-8h4"></path>
                          </svg>
                        </div>
                      </div>
                      <div class="px-4">
                        <h3 class="text-2xl font-semibold text-white mb-1 group-hover:text-blue-300 transition-colors duration-200">Process & Analyze Data</h3>
                        <p class="text-base text-gray-300 group-hover:text-gray-200">Configure processing settings and run analysis</p>
                      </div>
                      <div class="flex-shrink-0">
                        <div class="w-8 h-8 bg-gray-600 rounded-lg flex items-center justify-center group-hover:bg-blue-500 transition-colors duration-200">
                          <svg class="w-5 h-5 text-blue-400 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2 12h4l3-8 4 18 3-8h4"></path>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </button>
                </div>

                <!-- Post-Processing Options -->
                <div v-if="processingResults && !isProgressBarVisible" class="w-full">
                  <div class="flex flex-col items-center text-center">
                    <!-- Action Buttons -->
                    <div class="flex justify-center items-center gap-4 w-full max-w-none mx-auto">
                      <button
                        @click="exploreData"
                        class="group bg-gray-600 border border-gray-500 rounded-lg shadow-sm w-32 py-3 h-28 hover:shadow-md hover:border-gray-400 transition-all duration-200 text-white font-medium hover:text-blue-300"
                      >
                        <div class="flex flex-col items-center text-center">
                          <div class="w-8 h-8 bg-gray-500 rounded-lg flex items-center justify-center mb-2 group-hover:bg-gray-400 transition-colors duration-200">
                            <svg class="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                            </svg>
                          </div>
                          <span class="text-sm whitespace-nowrap">Explore</span>
                        </div>
                      </button>
                      
                      <button
                        @click="showAdherenceReport"
                        class="group bg-gray-600 border border-gray-500 rounded-lg shadow-sm w-32 py-3 h-28 hover:shadow-md hover:border-gray-400 transition-all duration-200 text-white font-medium hover:text-blue-300"
                      >
                        <div class="flex flex-col items-center text-center">
                          <div class="w-8 h-8 bg-gray-500 rounded-lg flex items-center justify-center mb-2 group-hover:bg-gray-400 transition-colors duration-200">
                            <svg class="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                            </svg>
                          </div>
                          <span class="text-sm whitespace-nowrap">Daily Tracking</span>
                        </div>
                      </button>
                      
                      <button
                        @click="showCurveResults"
                        class="group bg-gray-600 border border-gray-500 rounded-lg shadow-sm w-32 py-3 h-28 hover:shadow-md hover:border-gray-400 transition-all duration-200 text-white font-medium hover:text-blue-300"
                      >
                        <div class="flex flex-col items-center text-center">
                          <div class="w-8 h-8 bg-gray-500 rounded-lg flex items-center justify-center mb-2 group-hover:bg-gray-400 transition-colors duration-200">
                            <svg class="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2 12h4l3-8 4 18 3-8h4"></path>
                            </svg>
                          </div>
                          <span class="text-sm whitespace-nowrap">Curve Results</span>
                        </div>
                      </button>
                      
                      <button
                        @click="showHistogramResults"
                        class="group bg-gray-600 border border-gray-500 rounded-lg shadow-sm w-32 py-3 h-28 hover:shadow-md hover:border-gray-400 transition-all duration-200 text-white font-medium hover:text-blue-300"
                      >
                        <div class="flex flex-col items-center text-center">
                          <div class="w-8 h-8 bg-gray-500 rounded-lg flex items-center justify-center mb-2 group-hover:bg-gray-400 transition-colors duration-200">
                            <svg class="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                            </svg>
                          </div>
                          <span class="text-sm whitespace-nowrap">Temp & Motion</span>
                        </div>
                      </button>
                      
                      <button
                        @click="downloadData"
                        class="group bg-gray-600 border border-gray-500 rounded-lg shadow-sm w-32 py-3 h-28 hover:shadow-md hover:border-gray-400 transition-all duration-200 text-white font-medium hover:text-blue-300"
                      >
                        <div class="flex flex-col items-center text-center">
                          <div class="w-8 h-8 bg-gray-500 rounded-lg flex items-center justify-center mb-2 group-hover:bg-gray-400 transition-colors duration-200">
                            <svg class="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                            </svg>
                          </div>
                          <span class="text-sm whitespace-nowrap">Download Results</span>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Show Processing when in progress -->
                <ProgressBar 
                  v-if="showStatusBar && processing" 
                  text="Processing data..." 
                  width="85%" 
                  :progress="processingProgress"
                />
                <!-- Info message below progress bar (appears/disappears in sync with progress bar) -->
                <div v-if="showStatusBar && processing" class="mt-4 flex items-center justify-center">
                  <div class="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 max-w-4xl">
                    <svg class="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <p class="text-sm text-blue-800 whitespace-nowrap">
                      Processing may take between 2 and 10 minutes. You can leave and log back in later to view completed results.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Data at a Glance Section -->
          <div class="border-t border-gray-200 pt-6">
            <!-- Data Preview Header with Toggle -->
            <button 
              @click="toggleDataGlanceExpansion" 
              class="w-full flex items-center justify-between mb-4 bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg p-4 border border-gray-200"
            >
              <div class="flex items-center">
                <h3 class="text-lg font-medium text-gray-900">Data Preview</h3>
              </div>
              <svg 
                class="w-5 h-5 text-gray-500 transition-transform duration-200" 
                :class="isDataGlanceExpanded ? 'rotate-180' : ''"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <!-- Collapsible Data Preview Content -->
            <div v-show="isDataGlanceExpanded">
              <div class="overflow-auto max-h-80 border border-gray-200 rounded-lg">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50 sticky top-0">
                    <tr>
                      <th v-for="header in dataHeaders" :key="header" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {{ header }}
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="(row, index) in dataPreview.slice(0, 50)" :key="index">
                      <td v-for="header in dataHeaders" :key="header" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {{ row[header] }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <!-- Table Note -->
              <div class="mt-6 text-xs text-gray-600 italic">
                <p>Preview restricted to 50 rows.</p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>



    <!-- Process & Analyze Data Modal -->
    <ProcessDataModal
      :isVisible="showProcessSettingsModal"
      :processing="processing"
      :processingSettings="processingSettings"
      @close="showProcessSettingsModal = false"
      @process="handleProcessData"
    />



    <!-- Download Data Modal -->
    <BaseModal :isVisible="showDownloadModal" @close="showDownloadModal = false" width="lg">
      <template #header>Download Results</template>
      <template #content>
        <p class="text-sm text-gray-500 mb-6">Click the download button next to each data type to download as an Excel file</p>
        
        <div class="space-y-4">
          <!-- Curve Features -->
          <div class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" v-if="processingResults?.curve_features?.length > 0">
            <div class="flex items-center">
              <label class="text-sm font-medium text-gray-700">
                Curve Features
              </label>
              <span class="ml-2 text-xs text-gray-500">({{ processingResults.curve_features?.length || 0 }} curves)</span>
            </div>
            <button
              @click="downloadCurveFeatures"
              class="p-2 text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title="Download Curve Features"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </button>
          </div>
          
          <!-- Day Level Data -->
          <div class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" v-if="processingResults?.day_level_data?.length > 0">
            <div class="flex items-center">
              <label class="text-sm font-medium text-gray-700">
                Day Level Data
              </label>
              <span class="ml-2 text-xs text-gray-500">({{ processingResults.day_level_data?.length || 0 }} days)</span>
            </div>
            <button
              @click="downloadDayLevelData"
              class="p-2 text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title="Download Day Level Data"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </button>
          </div>
          
          <!-- Processing Summary -->
          <div class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" v-if="processingResults?.processing_summary">
            <div class="flex items-center">
              <label class="text-sm font-medium text-gray-700">
                Processing Summary
              </label>
            </div>
            <button
              @click="downloadProcessingSummary"
              class="p-2 text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              title="Download Processing Summary"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </button>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-end space-x-3">
          <button
            @click="showDownloadModal = false"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Close
          </button>
        </div>
      </template>
    </BaseModal>

    <ColumnSelectionModal
      :isVisible="showDayColumnModal"
      title="Select Day-Level Columns"
      :data="processingResults?.day_level_data || []"
      :groups="dayExportGroups"
      :descriptions="dayDescriptions"
      :lockedColumns="dayLockedColumns"
      :presets="dayPresets"
      sheetName="Day Level Data"
      :filename="dayFilename"
      @close="showDayColumnModal = false"
    />

    <ColumnSelectionModal
      :isVisible="showCurveColumnModal"
      title="Select Curve Feature Columns"
      :data="processingResults?.curve_features || []"
      :groups="curveExportGroups"
      :descriptions="curveDescriptions"
      :presets="curvePresets"
      sheetName="Curve Features"
      :filename="curveFilename"
      :extraSheets="curveExtraSheets"
      @close="showCurveColumnModal = false"
    />

  </div>
</template>

<script>
import axios from 'axios'
import BaseModal from '../../components/BaseModal.vue'
import ProcessDataModal from '../../components/ProcessDataModal.vue'
import ColumnSelectionModal from '../../components/ColumnSelectionModal.vue'
import ProgressBar from '../../components/ProgressBar.vue'
import Title from '../../components/Title.vue'
import { buildApiUrl, API_ENDPOINTS } from '../../config/api.js'
import { generateVariableKeySheet, featureCategories as curveFeatureCategories } from '../../utils/curveFeaturesVariableKey.js'
import {
  CURVE_EXPORT_FEATURE_GROUPS,
  buildEssentialCurveExportColumns,
  buildDefaultCurveExportColumns,
  buildAllCurveExportColumns,
  buildCurveDescriptions
} from '../../utils/curveExportFeatureRegistry.js'
import {
  DAY_EXPORT_FEATURE_GROUPS,
  buildDefaultDayExportColumns,
  buildEssentialDayExportColumns,
  buildAllDayExportColumns,
  isLockedExportColumn
} from '../../utils/dayExportFeatureRegistry.js'
import { downloadSubjectProcessingSummary } from '../../utils/processingSummaryExportUtils.js'
import dayFeatureDescriptions from '../../generated/dayFeatureDescriptions.json'
import { assertJobIdentityMatchesKey, validateProcessingResultsIdentity } from '../../utils/resultsS3Key.js'
import { useLabPath } from '../../composables/useLabPath.js'

export default {
  name: 'SubjectDashboard',
  inject: ['globalState', 'updateGlobalState', 'setBackButton', 'hideBackButton'],
  setup() {
    const toLab = useLabPath()
    return { toLab }
  },
  components: {
    BaseModal,
    ProcessDataModal,
    ColumnSelectionModal,
    ProgressBar,
    Title
  },
  data() {
    return {
      datetimeContract: null,
      configuredData: null,
      dataHeaders: [],
      dataPreview: [],
      showProcessSettingsModal: false,
      showDownloadModal: false,
      showDayColumnModal: false,
      showCurveColumnModal: false,

      processing: false,
      processingResults: null,
      showStatusBar: false,
      processingProgress: null, // 0-100 for progress bar, null for indeterminate
      pollCount: 0, // Track number of polls for progress estimation
      // Preserve initial metric values during processing
      preservedSamplingRate: null,
      preservedTotalObservations: null,
      preservedDayCount: null,
      // Accordion state
      isDataGlanceExpanded: false,
      processingSettings: {
        data_timezone: 'US/Central', // Default to Central Time
        adjust_for_gaps_and_non_wear: true, // Always enabled
        smooth_and_impute: true, // Always enabled
        identify_curves: true,  // Enable curve detection by default
        analyze_days: true,     // Enable day-level analysis by default
        match_events_to_curves: false,
        gaps_and_non_wear_attrs: {}, // Required for gaps and non-wear detection
        smooth_and_impute_attrs: {},
        curve_attrs: {
          curve_threshold: 'auto',
          manual_threshold: 10,
          default_threshold: 10,  // Max threshold permitted for auto mode (8-15)
          merge_curves_within_duration: 2
        },
        day_attrs: {
          day_start_hour: 0,
          enable_drinking_day_algorithm: false,
          day_export_columns: {}
        },
        event_attrs: {}
      }
    }
  },

  computed: {
    // Computed property to track if progress bar is visible
    isProgressBarVisible() {
      return this.showStatusBar && this.processing
    },
    // Computed properties for dashboard metrics - check both configuredData and processingResults
    // During processing, use preserved values; otherwise use current values
    displaySamplingRate() {
      if (this.processing && this.preservedSamplingRate !== null) {
        return this.preservedSamplingRate
      }
      return this.configuredData?.finalSamplingRate || 
             this.processingResults?.processing_summary?.sampling_rate || 
             this.processingResults?.data_metadata?.sampling_rate || 
             this.preservedSamplingRate || 
             0
    },
    displayTotalObservations() {
      if (this.processing && this.preservedTotalObservations !== null) {
        return this.preservedTotalObservations
      }
      return this.configuredData?.configuredRows || 
             this.processingResults?.data_metadata?.total_rows || 
             this.processingResults?.processed_dataset?.length || 
             this.preservedTotalObservations || 
             0
    },
    displayDayCount() {
      if (this.processing && this.preservedDayCount !== null) {
        return this.preservedDayCount
      }
      return this.configuredData?.dayCount || 
             this.processingResults?.day_level_data?.length || 
             this.preservedDayCount || 
             0
    },

    algorithmEnabled() {
      return Boolean(this.processingResults?.column_metadata?.drinking_day_algorithm_enabled)
    },
    subid() {
      return this.configuredData?.subid || 'Unknown'
    },
    datasetIdForFile() {
      return this.configuredData?.dataset_identifier || '001'
    },

    dayExportGroups() {
      return DAY_EXPORT_FEATURE_GROUPS
    },
    dayDescriptions() {
      return dayFeatureDescriptions
    },
    dayLockedColumns() {
      const all = DAY_EXPORT_FEATURE_GROUPS.flatMap((g) =>
        g.sections ? g.sections.flatMap((s) => s.columns) : (g.columns || [])
      )
      return all.filter((c) => isLockedExportColumn(c, this.algorithmEnabled))
    },
    dayPresets() {
      const alg = this.algorithmEnabled
      return {
        essential: () => buildEssentialDayExportColumns(alg),
        defaults: () => buildDefaultDayExportColumns(alg),
        all: () => buildAllDayExportColumns()
      }
    },
    dayFilename() {
      return `${this.subid}_${this.datasetIdForFile}_day_level_data.xlsx`
    },

    curveExportGroups() {
      return CURVE_EXPORT_FEATURE_GROUPS
    },
    curveDescriptions() {
      return buildCurveDescriptions(curveFeatureCategories)
    },
    curvePresets() {
      return {
        essential: buildEssentialCurveExportColumns,
        defaults: buildDefaultCurveExportColumns,
        all: buildAllCurveExportColumns
      }
    },
    curveFilename() {
      return `${this.subid}_${this.datasetIdForFile}_curve_features.xlsx`
    },
    curveExtraSheets() {
      const data = this.processingResults?.curve_features || []
      if (!data.length) return []
      return [{ name: 'Variable Key', worksheet: generateVariableKeySheet(data) }]
    }
  },

  watch: {
    // Update preserved values when processing completes
    processing(newVal, oldVal) {
      if (oldVal === true && newVal === false && this.processingResults) {
        // Processing just completed, update preserved values with final results
        const finalSamplingRate = this.processingResults?.processing_summary?.sampling_rate || 
                                   this.processingResults?.data_metadata?.sampling_rate || 
                                   this.preservedSamplingRate
        const finalTotalObservations = this.processingResults?.data_metadata?.total_rows || 
                                       this.processingResults?.processed_dataset?.length || 
                                       this.preservedTotalObservations
        const finalDayCount = this.processingResults?.day_level_data?.length || 
                              this.preservedDayCount
        
        if (finalSamplingRate !== null) this.preservedSamplingRate = finalSamplingRate
        if (finalTotalObservations !== null) this.preservedTotalObservations = finalTotalObservations
        if (finalDayCount !== null) this.preservedDayCount = finalDayCount
      }
    }
  },

  methods: {
    toggleDataGlanceExpansion() {
      this.isDataGlanceExpanded = !this.isDataGlanceExpanded
    },

    getStatusText() {
      if (this.processing) {
        return 'Processing in Progress'
      } else if (this.processingResults) {
        return 'Data Processed'
      } else {
        return 'Data Ready for Processing'
      }
    },


    async loadDatetimeContractOnce() {
      try {
        const cached = sessionStorage.getItem('datetimeContract')
        if (cached) {
          this.datetimeContract = JSON.parse(cached)
          return
        }
        const resp = await axios.get(buildApiUrl(API_ENDPOINTS.DATETIME_CONTRACT), {
          withCredentials: true
        })
        if (resp.data && resp.data.success) {
          this.datetimeContract = resp.data.data
          sessionStorage.setItem('datetimeContract', JSON.stringify(this.datetimeContract))
        }
      } catch (e) {
        console.warn('Failed to load datetime contract (endpoint not implemented):', e)
        // Set default datetime contract to prevent errors
        this.datetimeContract = {
          timezone: 'US/Central',
          format: 'ISO',
          default_start_hour: 0
        }
      }
    },

    
    async fetchFullDataset() {
      if (!this.configuredData.session_id) {
        throw new Error('No session ID available')
      }
      
      try {
        const response = await axios.post(buildApiUrl(API_ENDPOINTS.GET_FULL_DATASET), {
          session_id: this.configuredData.session_id
        }, {
          withCredentials: true
        })
        
        if (response.data.success) {
          return response.data.data
        } else {
          throw new Error(response.data.message || 'Failed to fetch full dataset')
        }
      } catch (error) {
        console.error('Error fetching full dataset:', error)
        throw error
      }
    },

    async fetchDataPreview() {
      // Fetch 50-row preview for large datasets stored in S3 or prior results
      if (!this.configuredData) {
        console.warn('No configured data available for data preview')
        return
      }
      
      console.log('🔍 fetchDataPreview called with configuredData:', {
        hasConfiguredData: !!this.configuredData,
        data_stored_in_s3: this.configuredData.data_stored_in_s3,
        session_id: this.configuredData.session_id ? this.configuredData.session_id.substring(0, 20) + '...' : null,
        s3_key: this.configuredData.s3_key,
        data_s3_key: this.configuredData.data_s3_key,
        allKeys: Object.keys(this.configuredData)
      })
      
      // For prior results, use s3_key; for active sessions, use session_id
      const hasSessionId = this.configuredData.session_id
      const hasS3Key = this.configuredData.s3_key
      
      if (!hasSessionId && !hasS3Key) {
        console.warn('❌ No session ID or S3 key available for data preview', {
          hasSessionId: !!hasSessionId,
          hasS3Key: !!hasS3Key,
          configuredDataKeys: Object.keys(this.configuredData)
        })
        return
      }
      
      // Only fetch if data is stored in S3 (large datasets with empty data array)
      // OR if it's a prior result (which always uses S3)
      // Check if data array is actually populated before skipping fetch
      const hasDataInMemory = this.configuredData.data && 
                               Array.isArray(this.configuredData.data) && 
                               this.configuredData.data.length > 0
      
      if (!this.configuredData.data_stored_in_s3 && !hasS3Key && hasDataInMemory) {
        console.log('✅ Data already loaded in memory, no need to fetch preview', {
          data_stored_in_s3: this.configuredData.data_stored_in_s3,
          hasS3Key: !!hasS3Key,
          dataLength: this.configuredData.data?.length || 0
        })
        return
      }
      
      // If data array is empty or missing, we need to fetch preview
      if (!hasDataInMemory) {
        console.log('📊 Data array is empty or missing, fetching preview from API...', {
          hasData: !!this.configuredData.data,
          dataLength: Array.isArray(this.configuredData.data) ? this.configuredData.data.length : 'not array'
        })
      }
      
      try {
        console.log('🔍 Fetching data preview from API...')
        const requestBody = {}
        
        if (hasS3Key) {
          // Prior result - use s3_key
          requestBody.s3_key = this.configuredData.s3_key
          console.log('📦 Fetching preview for prior result using s3_key:', this.configuredData.s3_key)
        } else {
          // Active session - use session_id
          requestBody.session_id = this.configuredData.session_id
          console.log('📊 Fetching preview for active session using session_id:', this.configuredData.session_id ? this.configuredData.session_id.substring(0, 20) + '...' : 'MISSING')
        }
        
        console.log('🔍 Request body:', { ...requestBody, session_id: requestBody.session_id ? requestBody.session_id.substring(0, 20) + '...' : null })
        const response = await axios.post(buildApiUrl(API_ENDPOINTS.GET_DATA_PREVIEW), requestBody)
        
        if (response.data.success && response.data.data) {
          this.dataPreview = response.data.data
          this.dataHeaders = response.data.headers || []
          console.log(`✅ Loaded ${this.dataPreview.length} rows for preview`)
        } else {
          console.warn('No preview data returned from API')
        }
      } catch (error) {
        console.error('Error fetching data preview:', error)
        // Non-critical - continue without preview
      }
    },



    handleProcessData(settings) {
      // This method is called by the ProcessDataModal component
      this.processingSettings = { ...this.processingSettings, ...settings }
      
      // Preserve current metric values before processing starts
      this.preservedSamplingRate = this.configuredData?.finalSamplingRate || 
                                    this.processingResults?.processing_summary?.sampling_rate || 
                                    this.processingResults?.data_metadata?.sampling_rate || 
                                    null
      this.preservedTotalObservations = this.configuredData?.configuredRows || 
                                         this.processingResults?.data_metadata?.total_rows || 
                                         this.processingResults?.processed_dataset?.length || 
                                         null
      this.preservedDayCount = this.configuredData?.dayCount || 
                                this.processingResults?.day_level_data?.length || 
                                null
      
      // Reset the state when processing actually begins
      this.showStatusBar = false
      this.processing = false
      this.processingResults = null
      this.processData()
    },

    async processData() {
      // Use global state as primary source of truth
      let configuredData = this.globalState?.configuredData || this.configuredData
      
      // Fallback: try to load directly from sessionStorage if not found
      if (!configuredData) {
        console.log('🔍 Trying to load configuredData directly from sessionStorage as fallback...')
        try {
          const storedData = sessionStorage.getItem('configuredData')
          if (storedData) {
            configuredData = JSON.parse(storedData)
            // Update both local and global state
            this.configuredData = configuredData
            this.updateGlobalState({ 
              configuredData: configuredData,
              subid: configuredData.subid || '',
              datasetId: configuredData.dataset_id || '',
              sessionId: configuredData.session_id || null
            })
            console.log('✅ Loaded configuredData from sessionStorage fallback')
          }
        } catch (error) {
          console.warn('Failed to load from sessionStorage fallback:', error)
        }
      }
      
      if (!configuredData) {
        console.error('No configured data available in global state, local state, or sessionStorage')
        console.error('Available sessionStorage keys:', Object.keys(sessionStorage))
        console.error('Available localStorage keys:', Object.keys(localStorage))
        return
      }

      try {
        assertJobIdentityMatchesKey(configuredData)
      } catch (identityError) {
        console.error('Job identity validation failed:', identityError)
        alert(identityError.message)
        return
      }
      
      // If this is a prior job, we can reprocess using processed_dataset from results.json
      // The backend will handle loading and cleaning the data
      const priorJobS3Key = configuredData.prior_job_s3_key || configuredData.s3_key || null
      
      // If reprocessing a prior job, we'll pass the s3_key to the backend
      // The backend will load results.json, clean processed_dataset, and create a new session
      if (configuredData.is_prior_job && priorJobS3Key) {
        console.log('🔄 Reprocessing prior job - backend will load and clean processed_dataset from:', priorJobS3Key)
      }

      // Calculate estimated processing time (overestimate for safety)
      const rowCount = configuredData.configuredRows || 1000
      const estimatedSeconds = Math.max(60, Math.floor((rowCount / 1000) * 30))  // More conservative: 30s per 1000 rows, min 60s
      const waitBeforePolling = Math.floor(estimatedSeconds * 0.3)  // Wait 30% of estimated time (50% quicker than previous 60%)
      
      console.log(`📊 Dataset: ${rowCount} rows, estimated ${estimatedSeconds}s`)
      console.log(`⏰ Will start polling after ${waitBeforePolling}s`)

      // Close modal and show status bar
      this.showProcessSettingsModal = false
      this.showStatusBar = true
      this.processing = true
      this.processingResults = null
      this.processingProgress = 5  // Start at 5% immediately to show activity
      this.pollCount = 0  // Reset poll count

      try {
        console.log('Sending data to API:', {
          subid: configuredData.subid,
          dataset_identifier: configuredData.dataset_identifier || '001',
          session_id: configuredData.session_id,
          sampling_rate: configuredData.finalSamplingRate,
          settingsKeys: Object.keys(this.processingSettings)
        })
        
        console.log('🎯 Processing settings being sent to server:', {
          identify_curves: this.processingSettings.identify_curves,
          analyze_days: this.processingSettings.analyze_days,
          curve_attrs: this.processingSettings.curve_attrs,
          day_attrs: this.processingSettings.day_attrs,
          allSettings: this.processingSettings
        })
        
        const requestPayload = {
          subid: configuredData.subid,
          dataset_identifier: configuredData.dataset_identifier || '001',
          sampling_rate: configuredData.finalSamplingRate || 1,
          settings: this.processingSettings
        }
        
        // If reprocessing a prior job, pass the s3_key (backend will handle session creation)
        if (priorJobS3Key && configuredData.is_prior_job) {
          requestPayload.prior_job_s3_key = priorJobS3Key
          requestPayload.auth_session_token = localStorage.getItem('session_token')
          // Processing session is null for prior jobs — backend creates a new one
          console.log('🔄 Reprocessing prior job - backend will load and clean processed_dataset from:', priorJobS3Key)
        } else {
          // Normal processing: include session_id
          requestPayload.session_token = configuredData.session_id  // Lambda expects session_token
          requestPayload.session_id = configuredData.session_id      // Include both for compatibility
          
          // If there's a prior_job_s3_key from re-uploading, pass it to delete after processing
          if (priorJobS3Key && !configuredData.is_prior_job) {
            requestPayload.prior_job_s3_key = priorJobS3Key
            console.log('🔄 Reprocessing with re-upload - will delete old results after completion:', priorJobS3Key)
          }
        }
        
        let response
        try {
          response = await axios.post(buildApiUrl(API_ENDPOINTS.ADVANCED_PROCESS), requestPayload, {
            withCredentials: true
          })
        } catch (firstErr) {
          const is502 = firstErr.response?.status === 502
          const isNetworkErr = firstErr.code === 'ERR_NETWORK' && !firstErr.response
          if (is502 || isNetworkErr) {
            await new Promise(r => setTimeout(r, 2000))
            response = await axios.post(buildApiUrl(API_ENDPOINTS.ADVANCED_PROCESS), requestPayload, {
              withCredentials: true
            })
          } else {
            throw firstErr
          }
        }

        if (response.status === 202 && response.data.success) {
          console.log('✅ Processing started, waiting before polling...')
          
          // For reprocessing prior jobs, the backend creates a new session
          // Extract the new session_id from the response if available
          let sessionIdForPolling = configuredData.session_id
          if (response.data.session_id || response.data.session_token) {
            sessionIdForPolling = response.data.session_id || response.data.session_token
            
            // Update configuredData with the new session_id for future use
            if (configuredData.is_prior_job) {
              configuredData.session_id = sessionIdForPolling
              configuredData.is_prior_job = false
              if (response.data.subid) {
                configuredData.subid = response.data.subid
              }
              if (response.data.dataset_identifier) {
                configuredData.dataset_identifier = response.data.dataset_identifier
                configuredData.dataset_id = response.data.dataset_identifier
              }
              sessionStorage.setItem('configuredData', JSON.stringify(configuredData))
              
              // CRITICAL: Update this.configuredData (component state) so storeProcessingResults() gets the updated values
              this.configuredData = { ...configuredData }
              
              // Also update global state
              this.updateGlobalState({
                configuredData: configuredData,
                sessionId: sessionIdForPolling,
                subid: configuredData.subid || '',
                datasetId: configuredData.dataset_identifier || ''
              })
            }
          }
          
          if (!sessionIdForPolling) {
            console.error('❌ No session_id available for polling')
            alert('Error: No session ID available. Please try again.')
            this.processing = false
            this.showStatusBar = false
            return
          }
          
          // Gradually increase progress during wait period (5% -> 10%)
          const waitStartTime = Date.now()
          const waitInterval = setInterval(() => {
            const elapsed = (Date.now() - waitStartTime) / 1000
            const waitProgress = Math.min(10, 5 + (elapsed / waitBeforePolling) * 5)
            this.processingProgress = Math.round(waitProgress)
          }, 500) // Update every 500ms
          
          // Wait before first poll
          await new Promise(resolve => setTimeout(resolve, waitBeforePolling * 1000))
          clearInterval(waitInterval)
          
          const batchJobId = response.data.job_id || response.data.job?.batch_job_id
          if (batchJobId) {
            console.log('📋 Batch job id for polling:', batchJobId)
          }

          // Start polling for status using the correct session_id and batch job id
          await this.pollProcessingStatus(
            configuredData.session_id,
            this.processingSettings,
            sessionIdForPolling,
            batchJobId
          )
        } else if (response.data && response.data.success) {
          // Backwards compatibility: if server returns 200 instead of 202 (local processing)
          this.processingResults = response.data.data
          
          // CRITICAL: For local processing (200 response), check if backend returned session_id for reprocessing
          let sessionIdFromResponse = null
          if (response.data.session_id || response.data.session_token) {
            sessionIdFromResponse = response.data.session_id || response.data.session_token
            
            // Update configuredData with the new session_id (same logic as 202 response)
            if (configuredData.is_prior_job) {
              configuredData.session_id = sessionIdFromResponse
              configuredData.is_prior_job = false
              if (response.data.subid) {
                configuredData.subid = response.data.subid
              }
              if (response.data.dataset_identifier) {
                configuredData.dataset_identifier = response.data.dataset_identifier
                configuredData.dataset_id = response.data.dataset_identifier
              }
              sessionStorage.setItem('configuredData', JSON.stringify(configuredData))
              
              // CRITICAL: Update this.configuredData (component state)
              this.configuredData = { ...configuredData }
              
              // Also update global state
              this.updateGlobalState({
                configuredData: configuredData,
                sessionId: sessionIdFromResponse,
                subid: configuredData.subid || '',
                datasetId: configuredData.dataset_identifier || ''
              })
            }
          }
          
          console.log('Processing results received:', {
            success: response.data.success,
            processingSummary: this.processingResults.processing_summary,
            samplingRate: this.processingResults.processing_summary?.sampling_rate,
            curveCount: this.processingResults.curve_features?.length || 0,
            dataRows: this.processingResults.processed_dataset?.length || 0,
            sessionIdFromResponse: sessionIdFromResponse?.substring(0, 20) + '...' || null
          })
          
          // Debug: Check the actual structure of the response
          console.log('🔍 Full processing results structure:', {
            allKeys: Object.keys(this.processingResults),
            curveFeatures: this.processingResults.curve_features,
            curveFeaturesType: typeof this.processingResults.curve_features,
            curveFeaturesLength: this.processingResults.curve_features?.length,
            hasCurveFeatures: !!this.processingResults.curve_features,
            processingSummaryKeys: this.processingResults.processing_summary ? Object.keys(this.processingResults.processing_summary) : []
          })
          
          // Debug: Check day-level data specifically
          console.log('🔍 Day-level data debug:', {
            hasDayLevelData: !!this.processingResults.day_level_data,
            dayLevelDataType: typeof this.processingResults.day_level_data,
            dayLevelDataLength: this.processingResults.day_level_data?.length || 0,
            dayLevelDataSample: this.processingResults.day_level_data?.[0] || 'None',
            dayLevelDataKeys: this.processingResults.day_level_data?.[0] ? Object.keys(this.processingResults.day_level_data[0]) : []
          })
          
          // Debug: Print day level columns
          if (this.processingResults.day_level_data?.length > 0) {
            console.log('📊 Sample day level data:', this.processingResults.day_level_data[0])
          }
          
          // Deep debug: Check if curve features have the expected structure
          if (this.processingResults.curve_features?.length > 0) {
            console.log('🎯 Curve features found in server response:', this.processingResults.curve_features)
            const firstCurve = this.processingResults.curve_features[0]
            console.log('🎯 First curve structure:', {
              allKeys: Object.keys(firstCurve),
              hasBegin: !!firstCurve.begin_CURVE,
              hasEnd: !!firstCurve.end_CURVE,
              hasDuration: !!firstCurve.duration_CURVE,
              hasPeak: !!firstCurve.peak_CURVE,
              beginValue: firstCurve.begin_CURVE,
              endValue: firstCurve.end_CURVE,
              durationValue: firstCurve.duration_CURVE,
              peakValue: firstCurve.peak_CURVE
            })
          } else {
            console.log('❌ No curve features found in server response')
            console.log('❌ This suggests the server may not be running curve detection')
          }
          
          // Store results (legacy path - backwards compatibility)
          // CRITICAL: Pass sessionIdFromResponse to storeProcessingResults for reprocessing scenarios
          // This ensures the new session_id is stored correctly
          this.storeProcessingResults(this.processingResults, configuredData, null, sessionIdFromResponse)
          
          console.log('Processing completed:', this.processingResults)
        } else {
          // Server returned an error response
          const errorMessage = response.data?.message || response.data?.error || `Server returned status ${response.status}`
          const errorCode = response.data?.error || 'PROCESSING_FAILED'
          console.error('❌ Processing failed:', errorMessage, 'Error code:', errorCode)
          
          this.processing = false
          this.processingResults = {
            success: false,
            errors: [{
              step: 'api_call',
              error: errorMessage,
              errorCode: errorCode,
              statusCode: response.status
            }]
          }
          
          // Show user-friendly error message
          alert(`Processing failed: ${errorMessage}\n\nPlease check your connection and try again. If the problem persists, contact support.`)
        }
      } catch (error) {
        console.error('❌ Error during processing:', error)
        const errorMessage = error.response?.data?.message || error.message || 'Failed to communicate with the server'
        const statusCode = error.response?.status || 'NETWORK_ERROR'
        
        this.processing = false
        this.processingResults = {
          success: false,
          errors: [{
            step: 'api_call',
            error: errorMessage,
            statusCode: statusCode
          }]
        }
        
        // Show user-friendly error message
        if (error.response?.status === 503) {
          alert('Processing service is currently unavailable. Please try again later or contact support.')
        } else if (error.response?.status === 500) {
          alert(`Processing failed: ${errorMessage}\n\nPlease try again. If the problem persists, contact support.`)
        } else {
          alert(`Failed to start processing: ${errorMessage}\n\nPlease check your connection and try again.`)
        }
      } finally {
        if (!this.processingResults || this.processingResults.errors) {
          this.processing = false
          this.showStatusBar = false
        }
      }
    },

    async pollProcessingStatus(sessionId, processingSettings, newSessionIdFromResponse = null, batchJobId = null) {
      // Use newSessionIdFromResponse if provided (for reprocessing), otherwise use sessionId
      // This handles the case where reprocessing creates a new session
      const actualSessionId = newSessionIdFromResponse || sessionId
      
      if (!actualSessionId) {
        console.error('❌ No session ID available for polling')
        this.processing = false
        this.processingResults = {
          success: false,
          errors: [{ step: 'polling', error: 'No session ID available for status polling' }]
        }
        return
      }
      
      console.log('📊 Starting status polling with session_id:', actualSessionId.substring(0, 20) + '...', batchJobId ? `job_id=${batchJobId}` : '')
      
      const maxPolls = 200  // Maximum 10 minutes of polling (200 × 3s) for large datasets
      this.pollCount = 0
      this.processingProgress = 10  // Start at 10% to show activity
      let consecutiveFailures = 0  // Track consecutive failures
      const maxConsecutiveFailures = 3  // Stop retrying after 3 consecutive failures
      
      while (this.pollCount < maxPolls) {
        try {
          let statusUrl = `${buildApiUrl(API_ENDPOINTS.PROCESS_STATUS)}?session_token=${encodeURIComponent(actualSessionId)}`
          if (batchJobId) {
            statusUrl += `&job_id=${encodeURIComponent(batchJobId)}`
          }
          const response = await axios.get(statusUrl, { withCredentials: true })
          
          if (response.data.success) {
            const job = response.data.job
            console.log(`📊 Poll ${this.pollCount + 1}: Status = ${job.status}`)
            
            if (job.status === 'completed') {
              // Processing complete
              // Get s3_key from job status if available (for on-demand fetching)
              const resultsS3Key = job.results_s3_key || null
              
              if (job.results_stored_in_s3 && !job.results) {
                // Large results stored in S3 - fetch them separately to avoid 413 error
                console.log('📦 Large results detected, fetching from S3...')
                if (resultsS3Key) {
                  console.log('📦 Results S3 key available:', resultsS3Key)
                }
                try {
                  const resultsResponse = await axios.get(
                    `${buildApiUrl(API_ENDPOINTS.RESULTS)}?session_token=${actualSessionId}`
                  )
                  if (resultsResponse.data.success) {
                    // Check if we got a presigned URL (results too large)
                    if (resultsResponse.data.presigned_url) {
                      console.log('📥 Fetching results from presigned S3 URL...')
                      const s3Response = await axios.get(resultsResponse.data.presigned_url)
                      this.processingResults = s3Response.data
                      // Pass s3_key to storeProcessingResults for on-demand fetching
                      // Pass actualSessionId if this was a reprocessing (new session created)
                      this.storeProcessingResults(s3Response.data, this.configuredData, resultsS3Key, actualSessionId)
                    } else if (resultsResponse.data.data) {
                      // Small results included directly
                      this.processingResults = resultsResponse.data.data
                      // Pass actualSessionId if this was a reprocessing (new session created)
                      this.storeProcessingResults(resultsResponse.data.data, this.configuredData, resultsS3Key, actualSessionId)
                    } else {
                      throw new Error('No results data available')
                    }
                  } else {
                    throw new Error(resultsResponse.data.message || 'Failed to fetch results')
                  }
                } catch (error) {
                  console.error('❌ Error fetching results from S3:', error)
                  this.processingResults = {
                    success: false,
                    errors: [{ step: 'results_fetch', error: 'Failed to retrieve results from S3' }]
                  }
                  // Set progress to 100% even on error (for visual consistency)
                  this.processingProgress = 100
                  // Wait briefly before hiding progress bar
                  await new Promise(resolve => setTimeout(resolve, 1000))
                  this.processing = false
                  return
                }
              } else {
                // Small results included directly
                this.processingResults = job.results
                // Pass s3_key even for small results (in case they're later moved to S3)
                // Pass actualSessionId if this was a reprocessing (new session created)
                this.storeProcessingResults(job.results, this.configuredData, resultsS3Key, actualSessionId)
              }
              
              // NOTE: S3 results are NOT cleaned up here - they need to remain available
              // for Explorer to fetch data via /api/get-data-for-time-range.
              // Cleanup should only happen when the session is explicitly closed.
              
              // Complete progress bar to 100%
              this.processingProgress = 100
              
              // Wait a moment for user to see the completed progress bar before showing results
              await new Promise(resolve => setTimeout(resolve, 1500)) // 1.5 second delay
              
              // Now hide progress bar and show dashboard
              this.processing = false
              return
              
            } else if (job.status === 'failed') {
              // Processing failed
              console.error('❌ Processing failed:', job.error)
              this.processingResults = {
                success: false,
                errors: [{ step: 'processing', error: job.error }]
              }
              // Set progress to 100% even on failure (for visual consistency)
              this.processingProgress = 100
              // Wait briefly before hiding progress bar
              await new Promise(resolve => setTimeout(resolve, 1000))
              this.processing = false
              return
            }
            
            // Success - reset consecutive failure counter
            consecutiveFailures = 0
            
            // Update progress based on poll count (gradually increase, cap at 90% until complete)
            this.pollCount++
            // Estimate progress: start at 10%, gradually increase to 90% based on polls
            // Use a logarithmic scale so it fills faster initially, then slows down
            const progressPercentage = Math.min(90, 10 + (this.pollCount / maxPolls) * 80)
            this.processingProgress = Math.round(progressPercentage)
            
            // Still processing, wait 3 seconds before next poll
            await new Promise(resolve => setTimeout(resolve, 3000))
            
          } else {
            console.error('Failed to get processing status:', response.data)
            break
          }
          
        } catch (error) {
          console.error('Error polling processing status:', error)
          consecutiveFailures++
          
          // Check if this is a persistent backend error (502, CORS) vs transient network error
          const is502Error = error.response?.status === 502 || error.code === 'ERR_BAD_RESPONSE'
          const isCorsError = error.message?.includes('CORS') || error.message?.includes('Access-Control-Allow-Origin')
          const isPersistentError = is502Error || isCorsError
          
          // Network errors (ERR_NETWORK_CHANGED) are often transient, but if we get too many
          // consecutive failures or a persistent backend error, stop retrying
          const isTransientNetworkError = !isPersistentError && 
                                         (error.code === 'ERR_NETWORK' || error.code === 'ERR_FAILED' || 
                                          error.message?.includes('Network Error'))
          
          // Stop retrying if:
          // 1. We've had too many consecutive failures (persistent issue)
          // 2. This is a backend error (502, CORS) indicating API Gateway/Lambda problem
          if (consecutiveFailures >= maxConsecutiveFailures || isPersistentError) {
            console.error(`❌ Stopping retries after ${consecutiveFailures} consecutive failures${isPersistentError ? ' (backend error)' : ''}`)
            break
          }
          
          // Retry transient network errors (but only if we haven't exceeded max failures)
          if (isTransientNetworkError && consecutiveFailures < maxConsecutiveFailures) {
            console.log(`⚠️ Transient network error (${consecutiveFailures}/${maxConsecutiveFailures}), retrying...`)
            await new Promise(resolve => setTimeout(resolve, 5000)) // Wait 5s before retry
            pollCount++ // Increment poll count but continue retrying
            continue
          }
          
          // If not a retryable error, break out
          break
        }
      }
      
      // Max polls reached or error occurred
      console.error('⏱️ Polling timeout or error')
      
      // Last attempt: Check if results are available (processing may have completed)
      try {
        console.log('🔄 Last attempt: Checking if results are available...')
        const checkResponse = await axios.get(
          `${buildApiUrl(API_ENDPOINTS.PROCESS_STATUS)}?session_token=${actualSessionId}`
        )
        
        if (checkResponse.data.success && checkResponse.data.job.status === 'completed') {
          console.log('✅ Results found! Processing had completed.')
          // Handle case where results are null (stored in S3 only)
          const jobResults = checkResponse.data.job.results
          if (jobResults) {
            this.processingResults = jobResults
          } else {
            // Results are stored in S3 only - create metadata object
            this.processingResults = {
              success: true,
              results_stored_in_s3: true,
              results_s3_key: checkResponse.data.job.results_s3_key,
              data_metadata: {
                total_rows: this.configuredData?.configuredRows || 0
              }
            }
          }
          // Pass actualSessionId if this was a reprocessing (new session created)
          this.storeProcessingResults(jobResults, this.configuredData, checkResponse.data.job.results_s3_key || null, actualSessionId)
          this.processing = false
          return
        }
      } catch (finalError) {
        console.error('Final check also failed:', finalError)
      }
      
      // No results found, show timeout error
      this.processing = false
      this.processingResults = {
        success: false,
        errors: [{ step: 'polling', error: 'Processing timeout - please refresh and try again' }]
      }
    },

    storeProcessingResults(processingResults, configuredData, s3Key = null, newSessionId = null) {
      // CRITICAL: Always read configuredData from sessionStorage first (source of truth)
      // This ensures we get the latest values after reprocessing, when processData() has updated sessionStorage
      // but this.configuredData might not be updated yet
      let latestConfiguredData = configuredData
      try {
        const storedConfiguredData = sessionStorage.getItem('configuredData')
        if (storedConfiguredData) {
          latestConfiguredData = JSON.parse(storedConfiguredData)
          console.log('🔍 storeProcessingResults: Using latest configuredData from sessionStorage:', {
            sessionId: latestConfiguredData.session_id?.substring(0, 20) + '...' || null,
            isPriorJob: latestConfiguredData.is_prior_job,
            hasSessionId: !!latestConfiguredData.session_id
          })
        }
      } catch (error) {
        console.warn('⚠️ Failed to read configuredData from sessionStorage in storeProcessingResults, using provided:', error)
      }

      const identityCheck = validateProcessingResultsIdentity(
        processingResults,
        latestConfiguredData,
        s3Key
      )
      if (!identityCheck.valid) {
        console.error('🛡️ Rejecting processing results — identity mismatch:', identityCheck.reason, {
          configuredSubid: latestConfiguredData?.subid,
          configuredDataset: latestConfiguredData?.dataset_identifier || latestConfiguredData?.dataset_id,
          resultsSubid: processingResults?.subid,
          resultsDataset: processingResults?.dataset_identifier || processingResults?.dataset_id,
          s3Key: s3Key || processingResults?.s3_key,
        })
        return
      }
      
      // Handle case where processingResults is null/undefined (large results stored in S3 only)
      if (!processingResults) {
        console.log('📦 Processing results are null (stored in S3 only), creating metadata-only entry')
        processingResults = {
          success: true,
          results_stored_in_s3: true,
          s3_key: s3Key || null,
          processed_dataset: undefined, // Explicitly set to undefined to avoid errors
          curve_features: [],
          day_level_data: [],
          processing_summary: {},
          data_metadata: {
            total_rows: latestConfiguredData?.configuredRows || 0
          }
        }
      }
      
      // Determine if we should store processed_dataset or just metadata + s3_key
      // For large datasets (>10MB estimated), store only metadata + s3_key
      const processedDatasetSize = processingResults?.processed_dataset?.length || 0
      const estimatedSizeMB = (processedDatasetSize * 1000) / (1024 * 1024) // Rough estimate: ~1KB per row
      
      // Use new session_id if provided (for reprocessing), otherwise use latestConfiguredData.session_id
      const sessionIdToStore = newSessionId || latestConfiguredData.session_id
      
      // DEBUG: Log session ID resolution for reprocessing scenarios
      console.log('🔍 storeProcessingResults called:', {
        hasNewSessionId: !!newSessionId,
        newSessionId: newSessionId?.substring(0, 20) + '...' || null,
        latestConfiguredDataSessionId: latestConfiguredData.session_id?.substring(0, 20) + '...' || null,
        originalConfiguredDataSessionId: configuredData.session_id?.substring(0, 20) + '...' || null,
        sessionIdToStore: sessionIdToStore?.substring(0, 20) + '...' || null,
        latestConfiguredDataIsPriorJob: latestConfiguredData.is_prior_job,
        originalConfiguredDataIsPriorJob: configuredData.is_prior_job,
        processingResultsHasSessionId: !!processingResults.session_id
      })
      
      // CRITICAL: If we have a new session_id (from reprocessing), this is now an active session
      // Remove s3_key since it's only for prior jobs (read-only). Active sessions use session_id.
      // If newSessionId is provided, it's definitely an active session (reprocessing created new session)
      // Otherwise, check if latestConfiguredData indicates it's not a prior job
      const isActiveSession = !!sessionIdToStore && (!!newSessionId || !latestConfiguredData.is_prior_job)
      
      // Only store s3_key if this is NOT an active session (i.e., it's a prior job)
      // For active sessions, we use session_id for all API calls
      // If newSessionId is provided (reprocessing), never store s3_key
      const shouldStoreS3Key = !isActiveSession && !newSessionId && (s3Key || processingResults.s3_key)
      const resultsS3Key = shouldStoreS3Key ? (s3Key || processingResults.s3_key) : null
      
      const shouldStoreFullDataset = estimatedSizeMB < 10 && !resultsS3Key // Store full dataset if <10MB and no s3_key provided
      
      // Preserve existing data_metadata if available, especially total_rows
      // This is critical because processed_dataset may be excluded for large datasets
      const existingDataMetadata = processingResults.data_metadata || {}
      const totalRows = existingDataMetadata.total_rows || processedDatasetSize
      
      const essentialData = {
        session_id: sessionIdToStore,  // Use new session_id if available (for reprocessing)
        subid: latestConfiguredData.subid,
        dataset_identifier: latestConfiguredData.dataset_identifier || '001',
        curve_features: processingResults.curve_features || [],
        day_level_data: processingResults.day_level_data || [],
        processing_summary: processingResults.processing_summary || {},
        data_timezone: this.processingSettings?.data_timezone || processingResults.data_timezone || 'US/Central',
        column_metadata: processingResults.column_metadata || {},
        drinking_day_score_meta: processingResults.drinking_day_score_meta || {},
        drinking_prediction_review: processingResults.drinking_prediction_review || null,
        adherence_review: processingResults.adherence_review || null,
        data_metadata: {
          total_rows: totalRows,
          start_time: existingDataMetadata.start_time || processingResults.processed_dataset?.[0]?.datetime,
          end_time: existingDataMetadata.end_time || processingResults.processed_dataset?.[processedDatasetSize - 1]?.datetime,
          has_data: existingDataMetadata.has_data !== undefined ? existingDataMetadata.has_data : !!processingResults.processed_dataset,
          ...existingDataMetadata // Preserve any other metadata fields (sampling_rate, etc.)
        }
      }
      
      // Store s3_key ONLY if this is a prior job (not an active session)
      // Active sessions should use session_id for all API calls
      if (resultsS3Key && !isActiveSession) {
        essentialData.s3_key = resultsS3Key
        console.log('📦 Storing s3_key for prior job (read-only):', resultsS3Key)
      } else if (isActiveSession) {
        // Explicitly remove s3_key for active sessions
        console.log('✅ Active session detected - using session_id only (s3_key removed):', sessionIdToStore?.substring(0, 20) + '...')
      }
      
      // Only store processed_dataset if dataset is small enough
      if (shouldStoreFullDataset && processingResults.processed_dataset) {
        essentialData.processed_dataset = processingResults.processed_dataset
        console.log('✅ Storing full processed_dataset (small dataset):', processedDatasetSize, 'rows')
      } else if (processedDatasetSize > 0) {
        console.log('📦 Excluding processed_dataset from storage (large dataset or s3_key provided):', processedDatasetSize, 'rows')
        console.log('📦 Data will be fetched on-demand via s3_key or session_id')
      }
      
      // SAFEGUARD: Only update frontend data if the completed job matches the currently loaded job
      // This prevents overwriting data when viewing a prior job while a different job completes
      const completedJobSubid = latestConfiguredData.subid
      const completedJobDatasetId = latestConfiguredData.dataset_identifier || latestConfiguredData.dataset_id || '001'
      
      // Check what job is currently loaded (from route params or sessionStorage/global state)
      let currentlyLoadedSubid = null
      let currentlyLoadedDatasetId = null
      
      // Try route params first (most reliable for prior jobs)
      if (this.$route?.query?.subid) {
        currentlyLoadedSubid = this.$route.query.subid
      }
      if (this.$route?.query?.dataset) {
        currentlyLoadedDatasetId = this.$route.query.dataset
      }
      
      // Fallback: check sessionStorage/global state for currently loaded job
      if (!currentlyLoadedSubid || !currentlyLoadedDatasetId) {
        try {
          const storedResults = sessionStorage.getItem('processingResults')
          if (storedResults) {
            const parsedResults = JSON.parse(storedResults)
            if (!currentlyLoadedSubid && parsedResults.subid) {
              currentlyLoadedSubid = parsedResults.subid
            }
            if (!currentlyLoadedDatasetId && parsedResults.dataset_identifier) {
              currentlyLoadedDatasetId = parsedResults.dataset_identifier
            }
          }
        } catch (error) {
          console.warn('⚠️ Failed to check current job from sessionStorage:', error)
        }
        
        // Also check global state
        if (!currentlyLoadedSubid && this.globalState?.subid) {
          currentlyLoadedSubid = this.globalState.subid
        }
        if (!currentlyLoadedDatasetId && this.globalState?.datasetId) {
          currentlyLoadedDatasetId = this.globalState.datasetId
        }
      }
      
      // Compare completed job with currently loaded job
      const jobsMatch = currentlyLoadedSubid && currentlyLoadedDatasetId &&
        completedJobSubid === currentlyLoadedSubid &&
        completedJobDatasetId === currentlyLoadedDatasetId
      
      if (!jobsMatch && (currentlyLoadedSubid || currentlyLoadedDatasetId)) {
        console.log('🛡️ SAFEGUARD: Completed job does not match currently loaded job - skipping frontend update', {
          completedJob: `${completedJobSubid}/${completedJobDatasetId}`,
          currentlyLoaded: `${currentlyLoadedSubid || 'unknown'}/${currentlyLoadedDatasetId || 'unknown'}`,
          note: 'Results are still saved to S3 and will appear in PriorJobsTable'
        })
        // Don't update sessionStorage/global state, but log that the job completed
        // PriorJobsTable will still show it because it fetches from S3
        return
      }
      
      console.log('✅ Completed job matches currently loaded job - updating frontend data', {
        job: `${completedJobSubid}/${completedJobDatasetId}`
      })
      
      try {
        sessionStorage.setItem('processingResults', JSON.stringify(essentialData))
        this.updateGlobalState({ processingResults: essentialData })
        
        // CRITICAL: Update this.processingResults to include the new session_id
        // This ensures that exploreData() and other methods can access the correct session_id
        if (!this.processingResults) {
          this.processingResults = {}
        }
        // Merge the essentialData into processingResults, preserving any existing fields
        this.processingResults = {
          ...this.processingResults,
          ...essentialData,
          session_id: essentialData.session_id, // Ensure session_id is set (especially important for reprocessing)
          s3_key: essentialData.s3_key || null // Ensure s3_key is set correctly (null for active sessions)
        }
        
      } catch (storageError) {
        console.warn('Storage quota exceeded, using minimal storage:', storageError)
        // Fallback: store only essential metadata (no processed_dataset)
        // Preserve existing data_metadata.total_rows if available
        const existingDataMetadata = processingResults.data_metadata || {}
        const totalRows = existingDataMetadata.total_rows || processedDatasetSize
        
        const minimalData = {
          session_id: sessionIdToStore,  // Use new session_id if available (for reprocessing)
          subid: latestConfiguredData.subid,
          dataset_identifier: latestConfiguredData.dataset_identifier || '001',
          curve_features: processingResults.curve_features || [],
          day_level_data: processingResults.day_level_data || [],
          processing_summary: processingResults.processing_summary || {},
          column_metadata: processingResults.column_metadata || {},
          drinking_day_score_meta: processingResults.drinking_day_score_meta || {},
          drinking_prediction_review: processingResults.drinking_prediction_review || null,
          adherence_review: processingResults.adherence_review || null,
          data_metadata: {
            total_rows: totalRows,
            has_data: existingDataMetadata.has_data !== undefined ? existingDataMetadata.has_data : processedDatasetSize > 0,
            ...existingDataMetadata // Preserve any other metadata fields
          }
        }
        
        // Include s3_key in minimal data ONLY if this is NOT an active session (prior job)
        if (resultsS3Key && !isActiveSession) {
          minimalData.s3_key = resultsS3Key
        }
        
        // SAFEGUARD: Apply same check for minimal data storage
        const completedJobSubidMinimal = latestConfiguredData.subid
        const completedJobDatasetIdMinimal = latestConfiguredData.dataset_identifier || latestConfiguredData.dataset_id || '001'
        
        let currentlyLoadedSubidMinimal = null
        let currentlyLoadedDatasetIdMinimal = null
        
        if (this.$route?.query?.subid) {
          currentlyLoadedSubidMinimal = this.$route.query.subid
        }
        if (this.$route?.query?.dataset) {
          currentlyLoadedDatasetIdMinimal = this.$route.query.dataset
        }
        
        if (!currentlyLoadedSubidMinimal || !currentlyLoadedDatasetIdMinimal) {
          try {
            const storedResults = sessionStorage.getItem('processingResults')
            if (storedResults) {
              const parsedResults = JSON.parse(storedResults)
              if (!currentlyLoadedSubidMinimal && parsedResults.subid) {
                currentlyLoadedSubidMinimal = parsedResults.subid
              }
              if (!currentlyLoadedDatasetIdMinimal && parsedResults.dataset_identifier) {
                currentlyLoadedDatasetIdMinimal = parsedResults.dataset_identifier
              }
            }
          } catch (error) {
            // Ignore errors
          }
          
          if (!currentlyLoadedSubidMinimal && this.globalState?.subid) {
            currentlyLoadedSubidMinimal = this.globalState.subid
          }
          if (!currentlyLoadedDatasetIdMinimal && this.globalState?.datasetId) {
            currentlyLoadedDatasetIdMinimal = this.globalState.datasetId
          }
        }
        
        const jobsMatchMinimal = currentlyLoadedSubidMinimal && currentlyLoadedDatasetIdMinimal &&
          completedJobSubidMinimal === currentlyLoadedSubidMinimal &&
          completedJobDatasetIdMinimal === currentlyLoadedDatasetIdMinimal
        
        if (!jobsMatchMinimal && (currentlyLoadedSubidMinimal || currentlyLoadedDatasetIdMinimal)) {
          console.log('🛡️ SAFEGUARD: Completed job does not match currently loaded job (minimal storage) - skipping frontend update', {
            completedJob: `${completedJobSubidMinimal}/${completedJobDatasetIdMinimal}`,
            currentlyLoaded: `${currentlyLoadedSubidMinimal || 'unknown'}/${currentlyLoadedDatasetIdMinimal || 'unknown'}`,
            note: 'Results are still saved to S3 and will appear in PriorJobsTable'
          })
          return
        }
        
        sessionStorage.setItem('processingResults', JSON.stringify(minimalData))
        this.updateGlobalState({ processingResults: minimalData })
        console.log('Minimal data stored (storage quota exceeded):', {
          session_id: minimalData.session_id,
          hasS3Key: !!minimalData.s3_key,
          allKeys: Object.keys(minimalData)
        })
      }
    },

    exploreData() {
      // CRITICAL: Always read from sessionStorage first (source of truth) to get the latest data
      // This is especially important after reprocessing, when storeProcessingResults() has updated sessionStorage
      // but this.processingResults might not have been updated yet
      let latestProcessingResults = this.processingResults
      try {
        const storedResults = sessionStorage.getItem('processingResults')
        if (storedResults) {
          const parsedResults = JSON.parse(storedResults)
          // Use stored results if available (they have the latest session_id after reprocessing)
          latestProcessingResults = parsedResults
        }
      } catch (error) {
        console.warn('⚠️ Failed to read from sessionStorage in exploreData, using component state:', error)
      }
      
      // Store essential metadata for the data explorer
      if (latestProcessingResults) {
        // Preserve existing data_metadata if available, especially total_rows
        // This is critical because processed_dataset may be excluded for large datasets
        const existingDataMetadata = latestProcessingResults.data_metadata || {}
        const totalRows = existingDataMetadata.total_rows || 
                         latestProcessingResults.processed_dataset?.length || 
                         0
        
        const essentialData = {
          // CRITICAL: Use session_id from latestProcessingResults (which has the new session_id after reprocessing)
          // Fallback to configuredData.session_id only if latestProcessingResults doesn't have it
          session_id: latestProcessingResults.session_id || this.configuredData.session_id,
          subid: this.configuredData.subid,
          dataset_identifier: this.configuredData.dataset_identifier || '001',
          curve_features: latestProcessingResults.curve_features || [],
          day_level_data: latestProcessingResults.day_level_data || [],
          processing_summary: latestProcessingResults.processing_summary || {},
          column_metadata: latestProcessingResults.column_metadata || {},
          drinking_day_score_meta: latestProcessingResults.drinking_day_score_meta || {},
          drinking_prediction_review: latestProcessingResults.drinking_prediction_review || null,
          adherence_review: latestProcessingResults.adherence_review || null,
          data_metadata: {
            total_rows: totalRows,
            start_time: existingDataMetadata.start_time || latestProcessingResults.processed_dataset?.[0]?.datetime,
            end_time: existingDataMetadata.end_time || latestProcessingResults.processed_dataset?.[latestProcessingResults.processed_dataset?.length - 1]?.datetime,
            has_data: existingDataMetadata.has_data !== undefined ? existingDataMetadata.has_data : !!latestProcessingResults.processed_dataset,
            ...existingDataMetadata // Preserve any other metadata fields
          },
          s3_key: latestProcessingResults.s3_key || null // Preserve s3_key if it exists (only for prior jobs)
        }
        
        // Note: Timeline data will be fetched by Explorer via API to get the true full dataset range
        try {
          sessionStorage.setItem('processingResults', JSON.stringify(essentialData))
          // Update global state
          this.updateGlobalState({ processingResults: essentialData })
        } catch (storageError) {
          console.warn('Storage quota exceeded, using minimal storage:', storageError)
          // Preserve existing data_metadata for minimal storage too
          const existingDataMetadata = latestProcessingResults.data_metadata || {}
          const totalRows = existingDataMetadata.total_rows || 
                           latestProcessingResults.processed_dataset?.length || 
                           0
          
          const minimalData = {
            // CRITICAL: Use session_id from latestProcessingResults (which has the new session_id after reprocessing)
            // Fallback to configuredData.session_id only if latestProcessingResults doesn't have it
            session_id: latestProcessingResults.session_id || this.configuredData.session_id,
            subid: this.configuredData.subid,
            dataset_identifier: this.configuredData.dataset_identifier || '001',
            curve_features: latestProcessingResults.curve_features || [],
            day_level_data: latestProcessingResults.day_level_data || [],
            processing_summary: latestProcessingResults.processing_summary || [],
            column_metadata: latestProcessingResults.column_metadata || {},
            drinking_day_score_meta: latestProcessingResults.drinking_day_score_meta || {},
            drinking_prediction_review: latestProcessingResults.drinking_prediction_review || null,
            adherence_review: latestProcessingResults.adherence_review || null,
            data_metadata: {
              total_rows: totalRows,
              start_time: existingDataMetadata.start_time || latestProcessingResults.processed_dataset?.[0]?.datetime,
              end_time: existingDataMetadata.end_time || latestProcessingResults.processed_dataset?.[latestProcessingResults.processed_dataset?.length - 1]?.datetime,
              has_data: existingDataMetadata.has_data !== undefined ? existingDataMetadata.has_data : !!latestProcessingResults.processed_dataset
            },
            s3_key: latestProcessingResults.s3_key || null // Preserve s3_key if it exists (only for prior jobs)
          }
          sessionStorage.setItem('processingResults', JSON.stringify(minimalData))
          // Update global state
          this.updateGlobalState({ processingResults: minimalData })
          
          // CRITICAL: Update this.processingResults to include the new session_id
          // This ensures that exploreData() and other methods can access the correct session_id
          if (!this.processingResults) {
            this.processingResults = {}
          }
          // Merge the minimalData into processingResults, preserving any existing fields
          this.processingResults = {
            ...this.processingResults,
            ...minimalData,
            session_id: minimalData.session_id, // Ensure session_id is set (especially important for reprocessing)
            s3_key: minimalData.s3_key || null // Ensure s3_key is set correctly (null for active sessions)
          }
          
          console.log('Minimal data stored due to storage quota:', {
            session_id: minimalData.session_id,
            curveCount: minimalData.curve_features?.length || 0,
            allKeys: Object.keys(minimalData),
            processingResultsSessionId: this.processingResults.session_id
          })
        }
      }
      
      // Navigate to data explorer in same tab
      console.log('✅ Navigating to Explorer in same tab')
      this.$router.push(this.toLab('/explorer'))
    },

    downloadData() {
      this.showDownloadModal = true
    },

    // Individual download methods using client-side Excel generation
    // NOTE: These methods use client-side XLSX generation for datasets that fit in memory.
    // For large datasets stored in S3 (where data_stored_in_s3 === true), use the
    // /api/download-data endpoint instead to fetch data from S3 and generate ZIP archives.
    downloadCurveFeatures() {
      this.showCurveColumnModal = true
    },

    downloadDayLevelData() {
      this.showDayColumnModal = true
    },

    downloadProcessingSummary() {
      try {
        const summary = this.processingResults?.processing_summary || {}
        if (!summary || Object.keys(summary).length === 0) {
          console.warn('No processing summary available to download')
          return
        }

        const subid = this.configuredData?.subid || this.processingResults?.subid || 'Unknown'
        const datasetId = this.configuredData?.dataset_identifier || this.processingResults?.dataset_identifier || '001'
        const results = this.globalState?.processingResults || this.processingResults
        downloadSubjectProcessingSummary(results, subid, datasetId)
        console.log('✅ Processing summary downloaded')
      } catch (error) {
        console.error('❌ Failed to download processing summary:', error)
        alert('Failed to download processing summary. Please try again.')
      }
    },

    showAnalysisResults() {
      // TODO: Implement show analysis results functionality
      console.log('Analysis Results clicked')
    },

    showAdherenceReport() {
      this.$router.push(this.toLab('/adherence-report'))
      console.log('Adherence Report clicked - navigating to adherence report page')
    },

    showCurveResults() {
      this.$router.push(this.toLab('/curve-results'))
      console.log('Curve Results clicked - navigating to curve results page')
    },

    showHistogramResults() {
      this.$router.push(this.toLab('/histogram-results'))
      console.log('Histogram Results clicked - navigating to histogram results page')
    },


  },
      async mounted() {
      await this.loadDatetimeContractOnce()
      
      // Set up back button to return to upload page
      // Note: No confirmation needed - processed data is saved in S3 and accessible via PriorJobsTable
      this.setBackButton({
        text: 'Upload New Data',
        route: '/' // Navigate directly to upload choice page
      })
      
      console.log('🔍 SubjectDashboard mounted - checking global state:', {
        hasGlobalConfiguredData: !!this.globalState?.configuredData,
        hasGlobalProcessingResults: !!this.globalState?.processingResults,
        globalSubid: this.globalState?.subid,
        globalDatasetId: this.globalState?.datasetId,
        globalSessionId: this.globalState?.sessionId
      })
      
      // Load configured data from global state
      if (this.globalState.configuredData) {
        const data = this.globalState.configuredData
        this.configuredData = data
        this.dataHeaders = data.headers || []
        this.dataPreview = data.data || []
        console.log('✅ Loaded configuredData from global state:', {
          session_id: data.session_id,
          subid: data.subid,
          dataset_id: data.dataset_id,
          hasData: !!data.data,
          dataLength: Array.isArray(data.data) ? data.data.length : 'not array',
          data_stored_in_s3: data.data_stored_in_s3,
          hasS3Key: !!data.s3_key,
          allKeys: Object.keys(data)
        })
      } else {
        console.log('⚠️ No configuredData found in global state')
        
        // Fallback: try to load from sessionStorage and update global state
        try {
          const storedData = sessionStorage.getItem('configuredData')
          if (storedData) {
            const data = JSON.parse(storedData)
            this.configuredData = data
            this.dataHeaders = data.headers || []
            this.dataPreview = data.data || []
            
            // Update global state with the loaded data
            this.updateGlobalState({
              configuredData: data,
              subid: data.subid || '',
              datasetId: data.dataset_id || '',
              sessionId: data.session_id || null
            })
            console.log('✅ Loaded configuredData from sessionStorage fallback and updated global state')
          }
        } catch (error) {
          console.warn('Failed to load configuredData from sessionStorage fallback:', error)
        }
      }
      
      // Load processing results from global state
      if (this.globalState.processingResults) {
        const results = this.globalState.processingResults
        this.processingResults = results
        console.log('✅ Loaded processingResults from global state:', {
          sessionId: results.session_id,
          curveCount: results.curve_features?.length || 0,
          hasProcessedData: !!results.data_metadata?.has_data,
          allKeys: Object.keys(results)
        })
      } else {
        console.log('⚠️ No processingResults found in global state')
        
        // Fallback: try to load from sessionStorage and update global state
        try {
          const storedResults = sessionStorage.getItem('processingResults')
          if (storedResults) {
            const results = JSON.parse(storedResults)
            this.processingResults = results
            
            // Update global state with the loaded results
            this.updateGlobalState({
              processingResults: results
            })
            console.log('✅ Loaded processingResults from sessionStorage fallback and updated global state')
          }
        } catch (error) {
          console.warn('Failed to load processingResults from sessionStorage fallback:', error)
        }
      }
      
      // Reset processing flag if we have results (processing is complete)
      if (this.processingResults) {
        this.processing = false
        this.showStatusBar = true  // Show the status bar with post-processing options
        console.log('🔄 Reset processing flag to false since we have results')
        console.log('🔄 Set showStatusBar to true to display post-processing options')
      }
      
      // Log final state
      console.log('SubjectDashboard mounted - final state:', {
        hasConfiguredData: !!this.configuredData,
        hasProcessingResults: !!this.processingResults,
        processing: this.processing,
        configuredDataKeys: this.configuredData ? Object.keys(this.configuredData) : [],
        processingResultsKeys: this.processingResults ? Object.keys(this.processingResults) : []
      })
      
      // Fetch data preview for large datasets stored in S3 or prior results
      // Also fetch if data array is empty or missing (even if data_stored_in_s3 is not explicitly set)
      const needsPreviewFetch = this.configuredData && (
        this.configuredData.data_stored_in_s3 || 
        this.configuredData.s3_key || 
        !this.configuredData.data || 
        (Array.isArray(this.configuredData.data) && this.configuredData.data.length === 0)
      )
      
      if (needsPreviewFetch) {
        console.log('🔍 Checking if preview fetch is needed:', {
          data_stored_in_s3: this.configuredData.data_stored_in_s3,
          hasS3Key: !!this.configuredData.s3_key,
          hasData: !!this.configuredData.data,
          dataLength: Array.isArray(this.configuredData.data) ? this.configuredData.data.length : 'not array'
        })
        if (this.configuredData.s3_key) {
          console.log('📦 Prior result detected - fetching preview data using s3_key...')
        } else {
          console.log('📊 Large dataset detected - fetching preview data using session_id...')
        }
        await this.fetchDataPreview()
      } else {
        console.log('✅ Preview fetch not needed - data already in memory:', {
          hasData: !!this.configuredData.data,
          dataLength: Array.isArray(this.configuredData.data) ? this.configuredData.data.length : 'not array',
          data_stored_in_s3: this.configuredData.data_stored_in_s3
        })
      }
      
      // Additional debugging for post-processing options
      if (this.processingResults) {
        console.log('🎯 Post-processing options should be visible:', {
          hasProcessingSummary: !!this.processingResults.processing_summary,
          hasCurveFeatures: !!this.processingResults.curve_features?.length,
          hasDataMetadata: !!this.processingResults.data_metadata,
          curveCount: this.processingResults.curve_features?.length || 0,
          dataRows: this.processingResults.data_metadata?.total_rows || 0
        })
        
        // Debug: Check if the UI should actually show the options
        console.log('🎯 UI state check:', {
          hasProcessingResults: !!this.processingResults,
          processingResultsType: typeof this.processingResults,
          processingResultsKeys: Object.keys(this.processingResults || {}),
          sessionId: this.processingResults.session_id,
          subid: this.processingResults.subid
        })
        
        // Debug: Check the processing flag and template condition
        console.log('🎯 Template condition check:', {
          processing: this.processing,
          processingResults: !!this.processingResults,
          condition: !this.processing && !!this.processingResults,
          shouldShow: !this.processing && !!this.processingResults
        })
      } else {
        console.log('❌ No processing results - post-processing options will not be visible')
        console.log('❌ This means the processingResults data is not being loaded from global state')
      }
    }
}
</script>